import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
	NavigationStepperRef,
} from '../../../../Components/Stepper/NavigationStepper';
import { usePatientTreatmentContext } from './ContextProvider';
import { useAppContext } from '../../../../Services/AppContext';
import ElectronicPrescriptionModal from '../../../Prescription/Components/ElectronicPrescriptionModal';
import EReceptList from '../../../../Components/eRecept/List/EReceptList';
import SearchMedicamentRecord from '../../../../Components/eRecept/SearchMedicamentRecord/SearchMedicamentRecord';
import SubTabNavigationStepper from '../../../../Components/Stepper/SubTabNavigationStepper/SubTabNavigationStepper';
import PatientTreatmentPageWrapper from './PatientTreatmentPageWrapper';
import { useAclContext } from '../../../Acl/Components/AclContextProvider';
import EPrescriptionWrapper from '../../../EPrescription/Components/EPrescriptionWrapper';
import ElectronicPrescriptionDetailWrapper from '../../../Prescription/Components/ElectronicPrescriptionDetail/ElectronicPrescriptionDetailWrapper';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import PermanentMedication from '../../../EPrescription/Components/PermanentMedication/PermanentMedication';
import useScope from '../../../Scope/Hooks/useScope';
import type { SearchMedicamentRecordListItemMedicamentScheme } from '../../../../Components/eRecept/SearchMedicamentRecord/SearchMedicamentRecordList/SearchMedicamentRecordListItemMedicament';
import type { SearchMedicamentRecordPatientFormValues } from '../../../../Components/eRecept/SearchMedicamentRecord/SearchMedicamentRecordPatientsDataFormScheme';
import { fetchMedicamentRecordReissueDefaults } from '../../../../Components/eRecept/Helpers/searchMedicamentRecordReissueDefaults';
import useNotification from '../../../../Hooks/useNotification.hook';

const PatientTreatmentPrescription: React.FC = () => {
	const { patientId } = usePatientTreatmentContext();
	const { isLatam, isMou } = useAppContext();
	const { isPoland } = useScope();
	const [showCreatePrescription, setShowCreatePrescription] = useState(false);
	const [steps, setSteps] = useState<NavigationStepperItemType[]>([]);
	const { isAllowed } = useAclContext();
	const canCreateErecept = isAllowed('erecept_erecept');
	const canSearchMedicamentRecord = isAllowed('erecept_lekovyzaznam');
	const canPermanentMedication = isAllowed('erecept_trvalamedikace');
	const prescriptionTabsRef = useRef<NavigationStepperRef>(null);
	const { showNotificationAlert } = useNotification();
	const [ereceptPrefillDefaults, setEreceptPrefillDefaults] =
		useState<unknown>(undefined);
	const [epWizardKey, setEpWizardKey] = useState(0);
	const issueAgainPendingNavRef = useRef(false);

	const handleMedicamentRecordIssueAgain = useCallback(
		async (
			medicament: SearchMedicamentRecordListItemMedicamentScheme,
			filterData: SearchMedicamentRecordPatientFormValues,
		) => {
			if (!canCreateErecept) {
				showNotificationAlert({
					content: 'patientTreatment.prescription.issueAgain.noPermission',
				});
				return;
			}
			if (!medicament.predpisId) {
				showNotificationAlert({
					content: 'patientTreatment.prescription.issueAgain.prepareFailed',
				});
				return;
			}
			try {
				const defaults = await fetchMedicamentRecordReissueDefaults(
					filterData,
					medicament.predpisId,
				);
				if (defaults === null) {
					showNotificationAlert({
						content: 'patientTreatment.prescription.issueAgain.catalogMiss',
					});
					return;
				}
				issueAgainPendingNavRef.current = true;
				setEreceptPrefillDefaults(defaults);
				setEpWizardKey(k => k + 1);
			} catch {
				showNotificationAlert({
					content: 'patientTreatment.prescription.issueAgain.prepareFailed',
				});
			}
		},
		[canCreateErecept, showNotificationAlert],
	);

	const handleMedicamentRecordIssueAgainRef = useRef(
		handleMedicamentRecordIssueAgain,
	);
	handleMedicamentRecordIssueAgainRef.current =
		handleMedicamentRecordIssueAgain;

	useEffect(() => {
		const tempSteps: NavigationStepperItemType[] = [];

		if (canCreateErecept) {
			const handleAfterPrescriptionCreated = (
				params: NavigationStepperComponentParams,
			) => {
				setEreceptPrefillDefaults(undefined);
				setEpWizardKey(k => k + 1);
				params.setActiveStepPath('issuedEprescriptions');
			};

			tempSteps.push({
				name: 'patientTreatment.prescription.create.title',
				key: 'create',
				disabled: isMou,
				component: params =>
					isLatam || isPoland ? (
						<ScrollContainer>
							<ElectronicPrescriptionDetailWrapper
								patientId={patientId}
								onCreate={() => handleAfterPrescriptionCreated(params)}
							/>
						</ScrollContainer>
					) : (
						<EPrescriptionWrapper
							key={`ep-wizard-${patientId}-${epWizardKey}`}
							patientId={patientId}
							defaultValues={ereceptPrefillDefaults}
							onPrescriptionCreated={() =>
								handleAfterPrescriptionCreated(params)
							}
							setIsLoading={params.setIsLoading}
						/>
					),
			});
			tempSteps.push({
				name: 'patientTreatment.prescription.issuedEprescriptions.title',
				key: 'issuedEprescriptions',
				disabled: isMou,
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<EReceptList
							patientId={patientId}
							setIsLoading={params.setIsLoading}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}
		if (canSearchMedicamentRecord) {
			tempSteps.push({
				name: 'patientTreatment.prescription.searchMedicalRecord.title',
				key: 'searchMedicalRecord',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<SearchMedicamentRecord
							patientId={patientId}
							setIsLoading={params.setIsLoading}
							onIssueAgainMedicament={(m, filterData) =>
								handleMedicamentRecordIssueAgainRef.current(m, filterData)
							}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}
		if (canPermanentMedication) {
			tempSteps.push({
				name: 'patientTreatment.prescription.permanentMedication.title',
				key: 'pernamentMedication',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<PermanentMedication patientId={patientId} />
					</PatientTreatmentPageWrapper>
				),
			});
		}
		setSteps(tempSteps);
	}, [
		canCreateErecept,
		canSearchMedicamentRecord,
		canPermanentMedication,
		patientId,
		isLatam,
		isPoland,
		isMou,
		epWizardKey,
		ereceptPrefillDefaults,
	]);

	useEffect(() => {
		if (!issueAgainPendingNavRef.current || !steps.length) {
			return;
		}
		const nav = prescriptionTabsRef.current;
		if (!nav) {
			return;
		}
		issueAgainPendingNavRef.current = false;
		nav.setActiveStepPath('create');
	}, [steps]);

	if (!steps.length) {
		return <></>;
	}

	return (
		<>
			<SubTabNavigationStepper
				ref={prescriptionTabsRef}
				steps={steps}
				initialStepPath={isMou ? 'searchMedicalRecord' : 'issuedEprescriptions'}
				canSkip={true}
			/>
			<ElectronicPrescriptionModal
				opened={showCreatePrescription}
				onClose={() => setShowCreatePrescription(false)}
				patientId={patientId}
			/>
		</>
	);
};

export default PatientTreatmentPrescription;
